import * as THREE from 'three';
import { gsap } from 'gsap';
import type { StageMode } from '../core/types';
import { ParticleField } from './ParticleField';
import type { Atmosphere, AtmosphereFn } from './atmosphere';
import { NetworkField, type NetworkState } from './NetworkField';

/** The closing scene's end card step; the opening network halo returns there. Keep in sync with ClosingScene. */
const CLOSING_END_CARD_STEP = 3;

/**
 * Three.js is the atmospheric layer: depth, dust and the opening network. Diagrams and copy live
 * in DOM/SVG so they stay crisp, accessible and cheap to change. Each scene registers an
 * atmosphere per step (see SceneContent.atmosphere), so the WebGL layer always has a
 * deterministic target state and knows nothing about which scenes exist.
 */
const NEUTRAL: Atmosphere = { pattern: 'ambient', density: 0.14, opacity: 0.05, cameraZ: 14 };

export class ThreeStage {
  public readonly element: HTMLDivElement;

  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 100);
  private readonly renderer: THREE.WebGLRenderer;
  private readonly timer = new THREE.Timer();
  private started = false;
  private readonly particles = new ParticleField(1200);
  private readonly network = new NetworkField(84);
  private readonly cameraBase = new THREE.Vector3(0, 0.1, 14);
  private readonly pointer = new THREE.Vector2();
  private readonly parallax = new THREE.Vector2();
  private readonly reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  private readonly atmospheres = new Map<string, AtmosphereFn>();
  private frame = 0;
  private mode: StageMode = 'opening';
  private step = -1;

  constructor(host: HTMLElement) {
    this.element = document.createElement('div');
    this.element.className = 'three-stage';
    host.appendChild(this.element);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.element.appendChild(this.renderer.domElement);

    this.camera.position.copy(this.cameraBase);
    this.scene.fog = new THREE.FogExp2(0x04070b, 0.018);
    this.scene.add(this.particles.points, this.network.group);

    this.resize();
    window.addEventListener('resize', this.resize);
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.render();
  }

  public registerAtmosphere(mode: StageMode, atmosphere: AtmosphereFn): void {
    this.atmospheres.set(mode, atmosphere);
  }

  public setMode(mode: StageMode, step = 0, immediate = false): void {
    const previousMode = this.mode;
    const previousStep = this.step;
    this.mode = mode;
    this.step = step;
    this.element.dataset.mode = mode;

    const atmosphere = this.atmospheres.get(mode)?.(step) ?? NEUTRAL;
    this.particles.setPattern(atmosphere.pattern);
    this.particles.setDensity(atmosphere.density);
    this.particles.setOpacity(atmosphere.opacity);
    this.particles.setSize(atmosphere.size ?? 0.07);

    // The camera never cuts, except on the very first frame.
    gsap.to(this.cameraBase, {
      z: atmosphere.cameraZ ?? 14,
      duration: this.started ? 2.2 : 0,
      ease: 'power2.inOut',
      overwrite: true,
    });

    this.started = true;
    if (mode === 'opening') this.openingNetwork(step, immediate, previousMode === 'opening' ? previousStep : -1);
    else if (mode === 'closing' && step >= CLOSING_END_CARD_STEP) {
      // The end card bookends the opening: the same network halo returns around the wordmark.
      const n = this.network.count;
      this.tweenNetwork({ reveal: n, form: 1, converge: 1, halo: 1, links: 0.3, opacity: 0.7 }, immediate ? 0 : 2.4);
    } else this.tweenNetwork({ opacity: 0 }, immediate ? 0 : 0.8);
  }

  public setAccent(color: string): void {
    this.particles.setColor(color);
  }

  public dispose(): void {
    cancelAnimationFrame(this.frame);
    window.removeEventListener('resize', this.resize);
    window.removeEventListener('pointermove', this.onPointerMove);
    gsap.killTweensOf([this.cameraBase, this.network.state]);
    this.particles.dispose();
    this.network.dispose();
    this.renderer.dispose();
  }

  /**
   * 0: a single breathing point
   * 1: points emerge and organise into a network
   * 2+: the network collapses to the centre and re-forms as a halo around the wordmark
   */
  private openingNetwork(step: number, immediate: boolean, previousStep: number): void {
    const n = this.network.count;
    const state = this.network.state;
    gsap.killTweensOf(state);

    if (step === 0) {
      this.tweenNetwork({ opacity: 1, reveal: 1, form: 0, converge: 0, halo: 0, links: 0 }, immediate ? 0 : 0.9);
      return;
    }

    if (step === 1) {
      if (immediate) {
        this.tweenNetwork({ opacity: 1, reveal: n, form: 1, converge: 0, halo: 0, links: 1 }, 0);
        return;
      }
      if (previousStep > 1) {
        this.tweenNetwork({ opacity: 1, reveal: n, form: 1, converge: 0, halo: 0, links: 1 }, 1.4);
        return;
      }
      gsap.to(state, { opacity: 1, duration: 0.4 });
      gsap.to(state, { reveal: n, duration: 4.2, ease: 'power1.in' });
      gsap.to(state, { form: 1, duration: 4.4, delay: 0.9, ease: 'power2.inOut' });
      gsap.to(state, { links: 1, duration: 3, delay: 1.4, ease: 'power1.inOut' });
      return;
    }

    const settled = { opacity: 0.85, reveal: n, form: 1, converge: 1, halo: 1, links: 0.55 };
    if (immediate || previousStep >= 2) {
      this.tweenNetwork(settled, immediate ? 0 : 0.6);
      return;
    }
    // Make sure everything is present before the collapse, even if step 1 was skipped quickly.
    gsap.to(state, { reveal: n, form: 1, opacity: 1, duration: 0.5 });
    gsap.to(state, { converge: 1, links: 0.2, duration: 1.05, ease: 'power3.in' });
    gsap.to(state, { halo: 1, links: 0.55, opacity: 0.85, duration: 1.8, delay: 1.05, ease: 'power3.out' });
  }

  private tweenNetwork(target: Partial<NetworkState>, duration: number): void {
    if (duration === 0) {
      gsap.killTweensOf(this.network.state);
      Object.assign(this.network.state, target);
      return;
    }
    gsap.to(this.network.state, { ...target, duration, ease: 'power2.inOut', overwrite: 'auto' });
  }

  private onPointerMove = (event: PointerEvent): void => {
    this.pointer.set((event.clientX / window.innerWidth) * 2 - 1, (event.clientY / window.innerHeight) * 2 - 1);
  };

  private resize = (): void => {
    const rect = this.element.getBoundingClientRect();
    const width = Math.max(1, rect.width || window.innerWidth);
    const height = Math.max(1, rect.height || window.innerHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  };

  private render = (): void => {
    this.frame = requestAnimationFrame(this.render);
    this.timer.update();
    const delta = Math.min(this.timer.getDelta(), 0.05);
    const time = this.timer.getElapsed();

    this.particles.update(time, delta);
    this.network.update(time);

    // Subtle pointer parallax: the scene has depth without anything spinning for its own sake.
    if (!this.reducedMotion) this.parallax.lerp(this.pointer, Math.min(1, delta * 2.2));
    this.camera.position.set(
      this.cameraBase.x + this.parallax.x * 0.45,
      this.cameraBase.y - this.parallax.y * 0.28,
      this.cameraBase.z,
    );
    this.camera.lookAt(0, 0, 0);
    this.renderer.render(this.scene, this.camera);
  };
}
